/**
 * Google Consent Mode v2 — shared mapping helper.
 * -------------------------------------------------
 * This is the SINGLE source of truth for translating the DJ SPY
 * consent object (`djspy_consent_v1` in localStorage, categories
 * `analytics` and `advertising`) into Google Consent Mode v2's four
 * parameters. Nothing else in the codebase should contain
 * consent-to-Google mapping logic.
 *
 * Mapping rules (STRICT — deliberately conservative):
 *   • Advertising category accepted → ad_storage / ad_user_data /
 *     ad_personalization all "granted".
 *   • Advertising category not accepted → all three advertising states
 *     stay "denied", regardless of any other category.
 *   • Analytics category accepted → analytics_storage "granted".
 *   • Analytics category not accepted → analytics_storage "denied".
 *   • Accepting analytics alone NEVER grants any advertising state.
 *
 * The consent object stored in `djspy_consent_v1` has the shape
 * created by `frontend/src/lib/ConsentContext.jsx::save`:
 *   {
 *     analytics:       boolean,
 *     advertising:     boolean,
 *     reason:          string,
 *     policy_version:  string,
 *     timestamp:       ISO string,
 *     gpc:             boolean
 *   }
 *
 * Anything else (undefined, missing keys, non-object) is treated as
 * "no consent recorded" → all four states remain "denied".
 */

export const CONSENT_STORAGE_KEY = "djspy_consent_v1";

// The four Google Consent Mode v2 states we control. Kept together so
// upstream consumers can iterate deterministically (e.g. for logging).
export const GOOGLE_CONSENT_KEYS = Object.freeze([
  "ad_storage",
  "ad_user_data",
  "ad_personalization",
  "analytics_storage",
]);

/**
 * Map a DJ SPY consent object → the four Google Consent Mode v2 states.
 *
 * Returns a fresh object every call (never a shared reference) so callers
 * can safely mutate the return value if needed. Never throws.
 */
export function getConsentStates(consentObj) {
  const advertising = !!(consentObj && consentObj.advertising === true);
  const analytics   = !!(consentObj && consentObj.analytics === true);
  return {
    ad_storage:         advertising ? "granted" : "denied",
    ad_user_data:       advertising ? "granted" : "denied",
    ad_personalization: advertising ? "granted" : "denied",
    analytics_storage:  analytics   ? "granted" : "denied",
  };
}

/**
 * Read the stored consent object from localStorage. Returns `null` if
 * nothing is stored, if the value can't be parsed, or if localStorage
 * is unavailable (SSR, privacy mode, etc.).
 *
 * Intentionally does NOT validate `policy_version` — the CMP owns that
 * check via `ConsentContext.load()`. This helper only needs to know the
 * current explicit choice so it can drive Google Consent Mode.
 */
export function getStoredConsent() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Push a `gtag('consent', 'update', ...)` call reflecting the given
 * consent object. Silently no-ops if `window.gtag` isn't defined yet
 * (the default consent state set in index.html will keep the four
 * parameters denied until this fires successfully).
 *
 * Return value:
 *   true  — states were dispatched to gtag
 *   false — gtag unavailable or dispatch threw (caller may retry later)
 */
export function applyGoogleConsent(consentObj) {
  if (typeof window === "undefined") return false;
  const states = getConsentStates(consentObj);
  try {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", states);
      return true;
    }
    // Fallback: gtag stub hasn't been created yet. Queue via dataLayer
    // so the update lands as soon as gtag.js parses (it drains the
    // same array). This should be rare because index.html defines the
    // gtag stub before any external script.
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(["consent", "update", states]);
      return true;
    }
  } catch {
    /* swallow — nothing to do */
  }
  return false;
}

/**
 * Convenience: read the stored consent AND apply it. Used by
 * TrackingLoader on mount for returning visitors.
 */
export function applyStoredConsent() {
  const stored = getStoredConsent();
  if (!stored) return false;
  return applyGoogleConsent(stored);
}

/**
 * Whether Enhanced Conversions user_data payloads are permitted for
 * the *currently stored* consent. Isolated so the Enhanced Conversions
 * gate in TrackingLoader has a single, testable predicate to check.
 */
export function isAdUserDataGranted(consentObj) {
  const source = consentObj !== undefined ? consentObj : getStoredConsent();
  return getConsentStates(source).ad_user_data === "granted";
}
